import {
  Input,
  Directive,
  TemplateRef,
  ViewContainerRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Inject
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { startWith, takeUntil } from 'rxjs/operators';
import {distinctUntilChanged, map, Subject, tap} from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TransferState } from '@angular/core';
import { SSR_DATA } from '@app/app-initializer';

type BreakpointInput = Array<keyof typeof Breakpoints>;

/**
 * Remove element from DOM and remove Angular view node unless within specified breakpoints. *All*
 *   breakpoints must be satisfied.
 * {@link https://material.angular.io/cdk/layout/api}
 *
 * @usage
 * ```
 * <div *hostelHopIfBreakpoint="['Small', 'HandsetPortrait']; else someOtherTemplate">
 *     // exists only when breakpoints are satisfied
 * </div>
 * ```
 */
@Directive({
  selector: '[hostelHopIfBreakpoint]',
})
export class NgIfBreakpointDirective implements OnInit, OnDestroy {

  public breakpoints: BreakpointInput = [];

  /**
   * If media does not match, show this template
   */
  private elseTemplate: TemplateRef<any>;

  private destroyed = new Subject<boolean>();

  @Input()
  set hostelHopIfBreakpoint(breakpoints: BreakpointInput) {
    this.breakpoints = breakpoints;
  }

  /**
   * Optionally set template to be displayed if query does not match
   */
  @Input()
  set hostelHopIfBreakpointElse(template: TemplateRef<any>) {
    this.elseTemplate = template;
  }

  private deviceType: string;

  constructor(
    private viewContainer: ViewContainerRef,
    private responsive: BreakpointObserver,
    private thenTemplate: TemplateRef<{}>,
    @Inject(PLATFORM_ID) private platformId: string,
    private deviceDetector: DeviceDetectorService,
    private transferState: TransferState
  ) {}

  ngOnInit() {

    const ssrData = this.transferState.get(SSR_DATA, {});
    this.deviceType = ssrData?.['DEVICE_TYPE'];

    const n = Math.random();
    // if (isPlatformServer(this.platformId)) {
      let initialValue;
      if (this.breakpoints.includes(Breakpoints.Small as any) || this.breakpoints.includes(Breakpoints.XSmall as any)) {
        if (this.deviceType === 'desktop') {
          initialValue = false;
        } else if (this.deviceType === 'mobile') {
          initialValue = true;
        } else {
          initialValue = this.deviceDetector.isMobile();
        }
      } else {
        if (this.deviceType === 'desktop') {
          initialValue = true;
        } else if (this.deviceType === 'mobile') {
          initialValue = false;
        } else {
          initialValue = this.deviceDetector.isDesktop();
        }
      }
      // if platform is server, then get request's user agent
      this.responsive.observe(
        this.breakpoints
      )
        .pipe(
          map(state => state.matches),
          distinctUntilChanged(),
          takeUntil(this.destroyed),
          startWith(initialValue)
        )
        .subscribe(result => {
          this.setTemplate(result, this.thenTemplate, this.elseTemplate);
        })
  }

  private setTemplate = (match: boolean, thenTemplate: TemplateRef<any>, elseTemplate: TemplateRef<any> | undefined): void => {
    const template = match ? thenTemplate : elseTemplate;
    this.viewContainer.clear();

    if (template) {
      this.viewContainer.createEmbeddedView(template);
    }
  }

  ngOnDestroy() {
    this.destroyed.next(true);
    this.destroyed.complete();
  }
}

